import { request, Logger } from '@nextgen-web-framework/core';
import type { RequestOptions } from '@nextgen-web-framework/core';
import { getPageReqObj, getContentReqObj, getLivePreviewReqObj } from '../../config/cmsEndPoints';
import { EntryObject, ErrorResponseError } from '../../models';
import { CustomRequestOptions } from '../../../types/amshopTypes';
import { GetContentPropTypes, GetPagesPropTypes, CMSDataType, Query, FetchLivePreviewProps } from './types/propTypes';
import referencesMapping from './referenceMapping';

const logger = Logger.getInstance();

type ApiResponsePromise = Promise<EntryObject | ErrorResponseError | null | unknown>;
const getReferencesQuery = (
  references?: string,
  referenceGroups?: string,
  params?: Array<Query>,
  isQueryPresent?: boolean,
) => {
  let reqUrl = '';
  if (references || referenceGroups) {
    let ref = `${isQueryPresent ? '&' : '?'}references=`;
    if (references) {
      ref += references;
    }
    if (referenceGroups) {
      ref += `,${referenceGroups}`;
    }
    reqUrl += ref;
  }
  if (params?.length) {
    let queryParams = '';
    queryParams = params
      .map(
        (param, index) => `${index === 0 && !(references || referenceGroups) ? '?' : '&'}${param.key}=${param.value}`,
      )
      .join('');
    reqUrl += queryParams;
  }
  return reqUrl;
};

const handleRequest = async <T>(req: RequestOptions, options?: CustomRequestOptions): Promise<T> => {
  if (options?.headers) {
    req.headers = options?.headers;
  }
  if (options?.baseURL) {
    req.url = `${options.baseURL}${req.url}`;
  }
  const response: T = await request(req);
  return Promise.resolve(response);
};

export const cmsApi = {
  async fetchContent(
    { uId, params, includeCount }: GetContentPropTypes,
    options?: CustomRequestOptions,
  ): ApiResponsePromise {
    const response = await this.fetchAllContent({ uId, params, includeCount }, options);
    if (Array.isArray(response)) {
      return response?.length > 0 ? response[0] : {};
    }
    return response;
  },

  async fetchAllContent(
    { uId, params, includeCount }: GetContentPropTypes,
    options?: CustomRequestOptions,
    includeReferences = true,
  ): ApiResponsePromise {
    try {
      const templateDetails = referencesMapping[uId];
      const { uId: uid, references = '', references_groups = '' } = templateDetails;
      const requestObj = getContentReqObj({
        uId: uid,
      });
      const referenceUrl = includeReferences
        ? getReferencesQuery(references, references_groups, params)
        : getReferencesQuery('', '', params);
      requestObj.url = `${requestObj.url}${referenceUrl}`;
      logger.info(`${'cms'} CONTENT SDK URL - `, requestObj.url);
      const response = await handleRequest<CMSDataType>(requestObj, options);
      if (includeCount) {
        return { data: response?.data, count: response?.count };
      }
      return response?.data;
    } catch (err) {
      logger.error(`${'cms'} CONTENT SDK FAILURE - `, err);
      throw err;
    }
  },
  async fetchLivePreview(
    { contentType, hash, entryId, params }: FetchLivePreviewProps,
    options?: CustomRequestOptions,
  ): ApiResponsePromise {
    try {
      const templateDetails = referencesMapping[contentType];
      const { uId, references = '', references_groups = '' } = templateDetails;
      const requestObj = getLivePreviewReqObj({
        contentType: uId,
        hash,
        entryId,
      });
      const referenceUrl = getReferencesQuery(references, references_groups, params, true);
      requestObj.url = `${requestObj.url}${referenceUrl}`;
      logger.info(`${'cms'} LIVE PREVIEW SDK URL - `, requestObj.url);
      const response = await handleRequest<CMSDataType>(requestObj, options);
      return response;
    } catch (err) {
      logger.error(`${'cms'} LIVE PREVIEW SDK FAILURE - `, err);
      throw err;
    }
  },
  async fetchPage({ slug, template }: GetPagesPropTypes, options?: CustomRequestOptions): ApiResponsePromise {
    const pages = await this.fetchAllPages({ slug, template }, options);
    if (Array.isArray(pages)) {
      return pages.length > 0 ? pages[0] : {};
    }
    return pages;
  },

  async fetchAllPages({ slug, template }: GetPagesPropTypes, options?: CustomRequestOptions): ApiResponsePromise {
    try {
      const requestObj = getPageReqObj({
        slug,
      });
      if (template) {
        const templateDetails = referencesMapping[template];
        const { uId, references = '', references_groups = '' } = templateDetails;
        requestObj.url = `${requestObj.url}?template=${uId}`;
        const referenceUrl = getReferencesQuery(references, references_groups, undefined, true);
        requestObj.url = `${requestObj.url}${referenceUrl}`;
      }
      const response = await handleRequest<CMSDataType>(requestObj, options);
      logger.info(`${'cms'} PAGE SDK URL - `, requestObj.url);
      return response?.data;
    } catch (err) {
      logger.error(`${'cms'} PAGE SDK FAILURE - `, err);
      throw err;
    }
  },
};

export { getReferencesQuery };
